import * as XLSX from 'xlsx';
import { Injectable, Inject } from "@fiqas/angular-utils";
import { FIQAssist } from "../app";
import { saveAs } from 'file-saver'
import { CompactServiceCase } from "../models/serviceCase";
import { TranslationService } from "./TranslationService";
import { AuthorizationService } from './authorizationService';

@Injectable({
    module: FIQAssist,
    selector: ExportService.selector
})
export class ExportService {
    public static selector = "ExportService"

    constructor(
        @Inject() private translationService: TranslationService,
        @Inject() private authorizationService: AuthorizationService
    ) {
    }

    saveExcelFromJson(content: CompactServiceCase[], params: any) {
        let fileNamePresent = params && params.fileName && params.fileName.length !== 0;
        let date = new Date();
        let formattedDate = [date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds()].join('');
        let fileName = fileNamePresent ? params.fileName : `FIQAssistResultsExport${formattedDate}`;

        let workbooksheet = this.cases_to_sheet(content);
        var workbook: XLSX.WorkBook = { SheetNames: ["SheetJS"], Sheets: { SheetJS: workbooksheet } };
        let wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'binary', cellDates: true });
        saveAs(new Blob([this.s2ab(wbout)], { type: "application/octet-stream" }), fileName + ".xlsx");
    }

    s2ab(s) {
        let buf = new ArrayBuffer(s.length);
        let view = new Uint8Array(buf);
        for (let i = 0; i != s.length; ++i) view[i] = s.charCodeAt(i) & 0xFF;
        return buf;
    }

    public cases_to_sheet(cases: CompactServiceCase[], opts?): XLSX.Sheet {
        var options = opts || {};
        var worksheet = ({});
        let cell = {};
        var range: XLSX.Range = ({ s: { c: 0, r: 0 }, e: { c: 0, r: cases.length } });
        var headers = this.authorizationService.currentRoleIn(["FQS", "SD"]) ? [
            "caseNumber",
            "parentCaseNumber",
            "fiqasOwnershipDesc", // ["FQS", "SD"]
            "caseOwnership", // ["FQS", "SD"]
            "start",
            "modifiedOn", // ["FQS", "SD"]
            "title",
            "caseCause", // ["FQS", "SD"]
            "spTsTask", // ["FQS", "SD"]
            "customerDesc",
            "customerReference",
            "contactPersonDesc",
            "createdBy",
            "status",
            "type",
            "severity",
            "priority",
            "budgetIndication", // ["FQS", "SD"]
            "approvedHoursMin", // ["FQS", "SD"]
            "approvedHoursMax", // ["FQS", "SD"]
            "actualHoursSpent", // ["FQS", "SD"]
            "approvedHoursCommentBySD", // ["FQS", "SD"]
            "feedbackStatus",
            "actionRequired",
            "requestedDeadline",
            "rememberMeDate", // ["FQS", "SD"]
            "rememberMeReason" // ["FQS", "SD"]
        ] : [
            "caseNumber",
            "parentCaseNumber",
            "start",
            "title",
            "customerDesc",
            "customerReference",
            "contactPersonDesc",
            "createdBy",
            "status",
            "type",
            "severity",
            "priority",
            "feedbackStatus",
            "actionRequired",
            "requestedDeadline"
        ];
        cases.forEach((value: CompactServiceCase, caseIndex) => {
            Object.keys(value).forEach((columnName) => {
                if (headers.indexOf(columnName) == -1)
                    return;
                var v = value[columnName];
                var t = 'z'; // emtpy cell
                var z;
                switch (columnName) {
                    case "start":
                    case "modifiedOn":
                        t = 'd';
                        z = 'dd/mm/yyyy hh:MM';
                        break;
                    case "requestedDeadline":
                    case "rememberMeDate":
                        t = 'd';
                        z = 'dd/mm/yyyy';
                        break;
                    case "caseNumber":
                        t = 'n';
                        // case has no parentCaseNumber but has child relate cases output it own id for easy project sorting/grouping in excel.
                        if (value['parentCaseNumber'] == undefined && value['childCaseNumbers'] != null && value['childCaseNumbers'].length > 0) {
                            worksheet[XLSX.utils.encode_cell({ c: headers.indexOf('parentCaseNumber'), r: caseIndex + 1 })] = cell = ((z) ? { t: t, v: v, z: z } : { t: t, v: v });
                        }
                        break;
                    case "parentCaseNumber":
                    case "approvedHoursMin":
                    case "approvedHoursMax":
                    case "actualHoursSpent":
                        t = 'n';
                        break;
                    case "caseOwnership":
                    case "actionRequired":
                    case "feedbackStatus":
                    case "severity":
                    case "priority":
                    case "caseCause":
                    case "type":
                        t = 's';
                        v = this.translationService.Translate(`XLS_${columnName}_${v}`);
                        break;
                    case "status":
                        t = 's';
                        v = this.translationService.Translate(v);
                        break;
                    default:
                        t = 's';
                    break;
                }
                worksheet[XLSX.utils.encode_cell({ c: headers.indexOf(columnName), r: caseIndex + 1 })] = cell = ((z) ? { t: t, v: v, z: z } : { t: t, v: v });
            })
        });
        range.e.c = headers.length;
        headers.map((value, index) => worksheet[XLSX.utils.encode_col(index) + "1"] = { t: 's', v: this.translationService.Translate(`XLS_${value}`), bold: true });
        worksheet['!ref'] = XLSX.utils.encode_range(range);
        worksheet['!cols'] = this.authorizationService.currentRoleIn(["FQS", "SD"]) ? [
            { wch: 8 }, // "caseNumber",
            { wch: 8 }, // "parentCaseNumber",
            { wch: 20 }, // "fiqasOwnershipDesc", // ["FQS", "SD"]
            { wch: 20 }, // "caseOwnership", // ["FQS", "SD"]
            { wch: 16 }, // "start",
            { wch: 16 }, // "modifiedOn", // ["FQS", "SD"]
            { wch: 32 }, // "title",
            { wch: 8 }, // "caseCause", // ["FQS", "SD"]
            { wch: 8 }, // "spTsTask", // ["FQS", "SD"]
            { wch: 20 }, // "customerDesc",
            { wch: 20 }, // "customerReference",
            { wch: 20 }, // "contactPersonDesc",
            { wch: 20 }, // "createdBy",
            { wch: 16 }, // "status",
            { wch: 8 }, // "type",
            { wch: 8 }, // "severity",
            { wch: 8 }, // "priority",
            { wch: 8 }, // "budgetIndication", // ["FQS", "SD"]
            { wch: 8 }, // "approvedHoursMin", // ["FQS", "SD"]
            { wch: 8 }, // "approvedHoursMax", // ["FQS", "SD"]
            { wch: 8 }, // "actualHoursSpent", // ["FQS", "SD"]
            { wch: 32 }, // "approvedHoursCommentBySD", // ["FQS", "SD"]
            { wch: 16 }, // "feedbackStatus",
            { wch: 8 }, // "actionRequired",
            { wch: 9 }, // "requestedDeadline",
            { wch: 9 }, // "rememberMeDate", // ["FQS", "SD"]
            { wch: 20 }, // "rememberMeReason" // ["FQS", "SD"]
        ] : [
            { wch: 8 }, // "caseNumber",
            { wch: 8 }, // "parentCaseNumber",
            { wch: 16 }, // "start",
            { wch: 32 }, // "title",
            { wch: 20 }, // "customerDesc",
            { wch: 20 }, // "customerReference",
            { wch: 20 }, // "contactPersonDesc",
            { wch: 20 }, // "createdBy",
            { wch: 16 }, // "status",
            { wch: 8 }, // "type",
            { wch: 8 }, // "severity",
            { wch: 8 }, // "priority",
            { wch: 16 }, // "feedbackStatus",
            { wch: 8 }, // "actionRequired",
            { wch: 9 } // "requestedDeadline",
        ];
        return worksheet;
    }
}
